import React from 'react'

import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import QuantityInput from '@/gf/components/inputs/Quantity'
import TextInput from '@/gf/components/inputs/Text'
import TextArea from '@/gf/components/TextArea'
import useSession from '../hooks/useSession'
import PartCombobox from './PartRequestsTableV2/PartCombobox'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import CluePartNumberSearch from '@/buyers/pages/CreateRequest/CluePartNumberSearch'
import JobCodeCombobox from '@/buyers/pages/Order/YourOrder/JobCodeCombobox'

type FormProps = React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>

export interface Form {
  mpn: string
  description: string
  quantity: number | null
  rfqQuantity?: number | null
  taskNumber?: string | null
}

export const initForm: Form = {
  mpn: '',
  description: '',
  quantity: 1,
}

export interface FormErrors {
  mpn?: string
  description?: string
  quantity?: string
  rfqQuantity?: string
  taskNumber?: string
}

export interface GqlError {
  message: string
}

export const Errors = (() => {
  const fromGqlErrors = (gqlErrors: GqlError[]): FormErrors =>
    gqlErrors.reduce((acc, gqlError) => {
      if (gqlError.message === 'mpn_or_description_required')
        return { ...acc, mpn: 'Part Number or Description is required.' }
      if (gqlError.message === 'mpn_too_long')
        return { ...acc, mpn: 'Part Number must be less than 256 characters.' }
      if (gqlError.message === 'description_too_long')
        return { ...acc, description: 'Description must be less than 256 characters.' }
      if (gqlError.message === 'task_number_too_long')
        return { ...acc, taskNumber: 'Job Code must be less than 256 characters.' }
      return acc
    }, {})

  return { fromGqlErrors }
})()

const PartForm = ({
  form,
  errors,
  onChange,
  onSubmit,
  children,
  useCluePartSearch = false,
  jobCodeWorkOrderId,
  ...props
}: {
  form: Form
  errors: FormErrors
  onChange: (form: Form) => void
  onSubmit: (form: Form) => void
  useCluePartSearch?: boolean
  /** M5 work order this part belongs to. Present only for AssetWorks orgs whose
   * request carries a work order — that is the only case with job codes to pick. */
  jobCodeWorkOrderId?: string
} & FormProps) => {
  const { featureFlags } = useSession()

  return (
    <Form onSubmit={onSubmit} {...props}>
      <div className="space-y-4">
        <Field label="Part Number" errors={errors.mpn}>
          {useCluePartSearch ? (
            <CluePartNumberSearch
              value={form.mpn}
              onChange={(mpn) => onChange({ ...form, mpn })}
              showInventory={false}
            />
          ) : featureFlags.assetworksApiSelectPart ? (
            <PartCombobox
              onPartSelected={async (part) => {
                onChange({ ...form, mpn: part.mpn, description: part.description })
              }}
              Trigger={<SelectorTrigger value={form.mpn} placeholder="Select part" />}
            />
          ) : (
            <TextInput value={form.mpn} setValue={(mpn) => onChange({ ...form, mpn })} />
          )}
        </Field>

        <Field label="Description" errors={errors.description}>
          <TextArea
            rows={4}
            value={form.description}
            setValue={(description) => onChange({ ...form, description })}
          />
        </Field>
        <Field label="Quantity" errors={errors.quantity}>
          <div className="w-48">
            <QuantityInput
              min={1}
              value={form.quantity}
              onBlur={() => {
                if (form.quantity === null) onChange({ ...form, quantity: 1 })
              }}
              setValue={(quantity) => onChange({ ...form, quantity })}
            />
          </div>
        </Field>
        {/* POC GEA-6992 kill-switch: job code is only editable while the auto-open lifecycle is on. */}
        {jobCodeWorkOrderId && featureFlags.assetworksAutoOpenPartRequests && (
          <Field label="Job Code" errors={errors.taskNumber}>
            <JobCodeCombobox
              jobCode={form.taskNumber ?? undefined}
              onJobCodeChange={(taskNumber) => onChange({ ...form, taskNumber })}
              workOrderId={jobCodeWorkOrderId}
            />
          </Field>
        )}
        {form.rfqQuantity !== undefined && (
          <Field label="RFQ Qty" errors={errors.rfqQuantity}>
            <div className="w-48">
              <QuantityInput
                min={0}
                max={form.quantity ?? undefined}
                value={form.rfqQuantity}
                onBlur={() => {
                  if (form.rfqQuantity === null) onChange({ ...form, rfqQuantity: 0 })
                }}
                setValue={(rfqQuantity) => onChange({ ...form, rfqQuantity })}
              />
            </div>
          </Field>
        )}
      </div>
      {children}
    </Form>
  )
}

export default PartForm
